import React, { useRef, useState } from "react";
import styled from "styled-components";
import { TAG_PALETTES } from "@lifeat/shared/task-management/TaskTagConstants";
import InfoIcon from "@common/Icons/InfoIcon";
import LifeAtCheckbox from "@common/LifeAtCheckbox/LifeAtCheckbox";
import { HorizontalDivider } from "@common/LifeAtDivider/LifeAtDivider";
import { EmojiData } from "@common/LifeAtEmojiPickerPopUpMenu/EmojiPicker";
import { LifeAtEmojiPickerPopUpMenu } from "@common/LifeAtEmojiPickerPopUpMenu/LifeAtEmojiPickerPopUpMenu";
import { PopUpMenuComponents } from "@common/LifeAtPopupMenuV2/PopUpMenuComponents";
import { LifeAtBodyPrimary, LifeAtBodySecondary } from "@common/LifeAtText/LifeAtText";
import { LifeAtTextInputV2 } from "@common/LifeAtTextInput/LifeAtTextInputV2";
import { FontSize } from "@themes/Fonts";
import Styles from "@themes/Styles";

export const TagCreationContentContainer = styled.div`
    display: flex;
    flex-direction: column;
    min-width: 260px;
    padding-bottom: 40px;
`;

export const HeaderRow = styled.div`
    display: flex;
    width: 100%;
    justify-content: space-between;
`;

export const InputSectionContainer = styled.div`
    display: flex;
    flex-direction: column;
    padding: 10px;
`;

export const EmojiNameInputContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

export const EmojiInputField = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

export const EmojiInputContainer = styled.div`
    border-radius: ${Styles.borderRadiusSharp};
    border: 1px solid ${(props) => props.theme.borderPrimary};
    padding: 4px;
    width: fit-content;
    min-width: 35px;
    display: flex;
    justify-content: center;
    cursor: pointer;
`;

const DefaultTagCheckboxLabel = styled.label`
    color: ${(props) => props.theme.textColorPrimary};
    font-size: ${FontSize.normal};
    margin-right: 7px;
`;

const DefaultTagInputContainer = styled.div`
    display: flex;
    align-items: center;
`;

export const ColorInputItem = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 7px 0;
`;

export const ColorPreview = styled.div<{ colorHex: string }>`
    height: 20px;
    width: 20px;
    border-radius: 5px;
    background-color: ${(props) => props.colorHex};
`;

export const ColorSelectorLabelContainer = styled.label`
    width: 100%;
    display: flex;
    gap: 10px;
`;

const EMOJI_INPUT_LABEL = "Emoji";
const TAG_NAME_INPUT_LABEL = "Tag name";
const SET_AS_DEFAULT_LABEL = "Set as default";
const DEFAULT_INPUT_INFO_TOOLTIP = "Each new task will be assigned this tag by default";

const DIVIDER_WIDTH_CSS_VALUE = "100%";

interface TagDetailMenuContentProps {
    readonly tagEmojiInputValue: string;
    readonly isTagDefault: boolean;
    readonly tagColorInputValue: string;
    readonly tagNameInputValue: string;
    readonly handleUpdateEmoji: (updatedEmoji: string) => void;
    readonly handleUpdateColor: (updatedColor: (typeof TAG_PALETTES)[number]["primary"]) => void;
    readonly handleUpdateDefault: (updatedDefault: boolean) => void;
    readonly handleUpdateTagName: (updatedTagName: string) => void;
    readonly tagNameErrorMessage: string | undefined;
}
export default function TagDetailMenuContent(props: TagDetailMenuContentProps) {
    const inputRef = useRef<HTMLInputElement | null>(null);

    const [isTagDefault, setIsTagDefault] = useState(props.isTagDefault);
    const toggleIsTagDefault = () => {
        const updatedIsTagDefault = !isTagDefault;
        setIsTagDefault(updatedIsTagDefault);
        props.handleUpdateDefault(updatedIsTagDefault);
    };

    return (
        <TagCreationContentContainer>
            <InputSectionContainer>
                <EmojiNameInputContainer>
                    <HeaderRow>
                        <EmojiInputField>
                            <LifeAtBodySecondary>{EMOJI_INPUT_LABEL}</LifeAtBodySecondary>
                            <LifeAtEmojiPickerPopUpMenu
                                onEmojiSelect={(e: EmojiData) => {
                                    props.handleUpdateEmoji(e.native);
                                }}
                            >
                                <EmojiInputContainer>{props.tagEmojiInputValue}</EmojiInputContainer>
                            </LifeAtEmojiPickerPopUpMenu>
                        </EmojiInputField>

                        <PopUpMenuComponents.CloseButton />
                    </HeaderRow>

                    <LifeAtTextInputV2
                        label={TAG_NAME_INPUT_LABEL}
                        value={props.tagNameInputValue}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => props.handleUpdateTagName(e.target.value)}
                        onClick={() => inputRef.current?.focus()}
                        errorMessage={props.tagNameErrorMessage}
                        showBorder={true}
                        ref={inputRef}
                    />
                </EmojiNameInputContainer>
            </InputSectionContainer>

            <HorizontalDivider widthCssValue={DIVIDER_WIDTH_CSS_VALUE} />
            <InputSectionContainer>
                <DefaultTagInputContainer>
                    <LifeAtCheckbox
                        checked={isTagDefault}
                        handleToggle={() => toggleIsTagDefault()}
                        id={"default-tag-checkbox"}
                    />
                    <DefaultTagCheckboxLabel htmlFor={"default-tag-checkbox"}>
                        {SET_AS_DEFAULT_LABEL}
                    </DefaultTagCheckboxLabel>
                    <InfoIcon tooltipId={"default-tag-info-tooltip"} tooltipText={DEFAULT_INPUT_INFO_TOOLTIP} />
                </DefaultTagInputContainer>
            </InputSectionContainer>

            <HorizontalDivider widthCssValue={DIVIDER_WIDTH_CSS_VALUE} />
            <InputSectionContainer>
                {TAG_PALETTES.map((colorPalette) => {
                    return (
                        <ColorInputItem key={`color-item-${colorPalette.label}`}>
                            <input
                                type="radio"
                                value={colorPalette.primary}
                                checked={colorPalette.primary === props.tagColorInputValue}
                                id={`tag-color-option-${colorPalette.label}`}
                                onChange={() => props.handleUpdateColor(colorPalette.primary)}
                            />
                            <ColorSelectorLabelContainer htmlFor={`tag-color-option-${colorPalette.label}`}>
                                <ColorPreview colorHex={colorPalette.primary} />
                                <LifeAtBodyPrimary>{colorPalette.label}</LifeAtBodyPrimary>
                            </ColorSelectorLabelContainer>
                        </ColorInputItem>
                    );
                })}
            </InputSectionContainer>
        </TagCreationContentContainer>
    );
}
